import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AccountMenu } from "@/components/account-menu";
import { AddToHomescreen } from "@/components/add-to-homescreen";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/user-avatar";
import { useRealtimeInvalidation } from "@/hooks/use-realtime-invalidation";
import { setUploadQueryClient } from "@/lib/upload-store";
import { UploadTray } from "@/components/upload-tray";
import { useSurface } from "@/providers/surface-provider";
import { Sidebar } from "./sidebar";
import { IconMenu2 } from "@tabler/icons-react";

export function RootLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { surface } = useSurface();
  useRealtimeInvalidation();
  const queryClient = useQueryClient();
  useEffect(() => setUploadQueryClient(queryClient), [queryClient]);

  return (
    <TooltipProvider delayDuration={300}>
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 h-12 bg-background border-b border-border flex items-center justify-between px-4 z-30 lg:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1 text-foreground"
          aria-label="Menu"
        >
          <IconMenu2 className="h-5 w-5" />
        </button>
        <span className="font-bold text-[15px]">SailGP</span>
        <div className="flex items-center gap-3">
          <AddToHomescreen variant="topbar" />
          <AccountMenu side="bottom" align="end">
            <button aria-label="Account settings">
              <UserAvatar className="w-[26px] h-[26px]" textClass="text-[10px]" />
            </button>
          </AccountMenu>
        </div>
      </div>

      {/* Main content */}
      {/* The restyle scope is the page column only: the sidebar, mobile top bar,
          upload tray and toaster sit outside <main> and keep the app theme. */}
      <main
        data-surface={surface ?? undefined}
        className="lg:ml-[256px] pt-12 lg:pt-0 min-h-screen"
      >
        <div className="px-4 sm:px-8 py-6 sm:py-8">
          <Outlet />
        </div>
      </main>

      <UploadTray />
      <Toaster />
    </div>
    </TooltipProvider>
  );
}
